import { Component, OnInit, Output, EventEmitter, Input, ElementRef, OnChanges, ViewChild } from '@angular/core';
import { MoneyFormat, Moment } from 'src/app/shared/utils';

import { StatusComponent } from 'src/app/shared/components/status/status.component';
import { BaseModel } from 'src/app/shared/models/base.model';
import { GridColumn, GridOption, GridTotal } from 'src/app/shared/utils/grid';
import { Status } from 'src/app/shared/interfaces';
import { NewLineKind } from 'typescript';

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent implements OnInit, OnChanges {
  @ViewChild('status') status: StatusComponent;

  @Input() columns: GridColumn[] = [];
  @Input() options: GridOption[] = [];
  @Input() resources: BaseModel[] = [];
  @Input() showTotal: boolean = false;
  @Input() emptyMessage: string = null;
  @Output() dblClickItem: EventEmitter<{ event: Event; item: BaseModel }> = new EventEmitter();
  @Output() columnClick: EventEmitter<{ event: Event; column: GridColumn }> = new EventEmitter();

  public total: GridTotal[] = [];
  public totalSelected: GridTotal[] = [];
  public showOptionOf: number = null;

  public booleanField: string;
  public selectField: string;

  private clickTime = 0;
  private clickId = 0;

  constructor(
    private elRef: ElementRef
  ) { }

  ngOnInit(): void {
    this.elRef.nativeElement.parentElement.style.padding = 0;
  }

  ngOnChanges() {
    if (this.resources.length > 0 && this.columns.length > 0) {
      const booleanCol = this.columns.find(a => a.type === 'boolean');
      if (booleanCol) {
        this.booleanField = booleanCol.field;
      }

      const selectCol = this.columns.find(a => a.type === 'select');
      if (selectCol) {
        this.selectField = selectCol.field;
      }

      if (this.showTotal) {
        this.total = [];
        for (let idx = 0; idx < this.columns.length; idx++) {
          const col = this.columns[idx];

          let vlrTotal = null;
          if (['money', 'currency', 'number'].includes(col.format)) {
            vlrTotal = this.resources.map(item => item[col.field]).reduce((a, b) => +a + +b, 0);
            vlrTotal = this.format(col.format, vlrTotal);
          }

          this.total.push(
            { cls: col.cls, format: col.format, title: col.title, style: col.style, type: col.type, value: vlrTotal }
          );
        }
      }
    }
  }

  @Input('status')
  set setStatus(status: Status | null) {
    if (!this.status) {
      return;
    }

    if (status) {
      this.status.setStatus(status);
      this.status.visible = true;
    } else {
      this.status.hide();
    }
  }

  public visibleColumns(): GridColumn[] {
    return this.columns.filter(a => !a.hidden);
  }

  public selectItem(event: Event, resource: BaseModel, column: GridColumn): void {
    resource['selected'] = !resource['selected'];

    this.totalSelected = [];

    // console.log(this.columns);
    const selectedItems = this.resources.filter(a => a['selected']);
    if (selectedItems.length > 0) {
      for (let idx = 0; idx < this.columns.length; idx++) {
        const col = this.columns[idx];

        let vlrTotal = null;
        if (['money', 'currency'].includes(col.format)) {
          vlrTotal = selectedItems.map(item => item[col.field]).reduce((a, b) => +a + +b, 0);
          vlrTotal = this.format(col.format, vlrTotal);
        }

        this.totalSelected.push(
          { cls: col.cls, format: col.format, title: col.title, style: col.style, type: col.type, value: vlrTotal }
        );
      }
    }

    if (column && column.clickEvent) {
      column.clickEvent(event, resource);
    }
  }

  public onDblClickItem(event: Event, item: BaseModel): void {
    if (this.clickTime === 0) {
      this.clickTime = new Date().getTime();
      this.clickId = item.id;
    } else {
      if (((new Date().getTime() - this.clickTime) < 500) && (this.clickId === item.id)) {
        if (this.dblClickItem) {
          this.dblClickItem.emit({ event, item });
        }
        this.clickId = 0;
      } else {
        this.clickTime = new Date().getTime();
        this.clickId = item.id;
      }
    }
  }

  onColumnClick(event: Event, column: GridColumn): void {
    if (this.columnClick) {
      this.columnClick.emit({ event, column });
    }
  }

  public setOptionsConfig(trIdx: number): void {
    this.showOptionOf = trIdx;
  }

  public hideOptions(event: Event): void {
    this.showOptionOf = null;
  }

  public getItemValue(item: BaseModel, column: GridColumn): any {
    let value = item;
    column.field.split('.').forEach(f => value = !!value ? value[f] : null);

    if (column.format) {
      return this.format(column.format, value);
    }
    return value;
  }

  private format(opt: string, value: any): any {
    switch (opt) {
      case 'money': return this.formatMoney(value); break;
      case 'currency': return this.formatCurrency(value); break;
      case 'date': return this.formatDate(value); break;
      case 'datetime': return this.formatDateTime(value); break;
      case 'phone': return this.formatPhone(value); break;
      default: return value; break;
    }
  }

  private formatMoney(value: number): string {
    return MoneyFormat(value || 0);
  }

  private formatCurrency(value: number): string {
    // TODO Rever forma de formatar moeda sem simbolo
    return MoneyFormat(value || 0).replace('R$', '').trim();
  }

  private formatDate(value: Date): string {
    if (!value) {
      return '-';
    }

    return Moment(value).format('DD/MM/YY');
  }

  private formatDateTime(value: Date): string {
    if (!value) {
      return '-';
    }

    return Moment(value).format('DD/MM/YY HH:mm');
  }

  private formatPhone(value: string): string {
    if (value.length === 14) {
      const country = value.slice(1, 3);
      const uf = value.slice(3, 5);
      const numberIni = value.slice(5, 10);
      const numberEnd = value.slice(10, 15);
      return `+${country} (${uf}) ${numberIni}-${numberEnd}`;
    } else {
      return value;
    }
  }

  getCls(item: BaseModel, colum: GridColumn): Object {
    const clss = [];
    if (colum.cls) {
      clss.push(colum.cls);
    }
    if (colum.getCls) {
      clss.push(colum.getCls(colum.field, item));
    }
    return clss.join(' ');
  }
}
